/**
 * SubBidPage — no-login page a subcontractor lands on from the RFQ email.
 * They read the scope, open the branded RFQ, and quote straight back.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, FileText, CheckCircle2, Send, Upload, XCircle, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

export default function SubBidPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    amount: "", duration_days: "", crew_size: "", inclusions: "", exclusions: "",
    bonded: false, contact_name: "", contact_phone: "", license_number: "", valid_days: 90,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/invite/${token}`);
      setData(r.data);
      setForm((f) => ({
        ...f, contact_name: r.data.invitation.contact_name || "",
        ...(r.data.invitation.bid || {}),
      }));
      if (r.data.invitation.status === "bid_received") setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "This quote request link is no longer valid.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.amount) return toast.error("Enter your lump-sum price");
    setBusy(true);
    try {
      await axios.post(`${API}/bid-desk/invite/${token}/bid`, form);
      setDone(true);
      toast.success("Quote submitted — thank you");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your quote");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/bid-desk/invite/${token}/bid/attachment`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const decline = async () => {
    const reason = window.prompt("No problem — anything you want us to know?") || "";
    try {
      await axios.post(`${API}/bid-desk/invite/${token}/decline`, { reason });
      toast.success("Thanks for letting us know");
      load();
    } catch {
      toast.error("Could not record that");
    }
  };

  if (error) {
    return <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <p className="text-slate-600" data-testid="sub-bid-error">{error}</p></div>;
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const { company, invitation, solicitation, milestones, scope } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sub-bid-page">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
              Subcontractor Request for Quote
            </p>
            <h1 className="text-2xl font-black mt-1">{invitation.trade}</h1>
            <p className="text-sm text-slate-300 mt-0.5">{solicitation.title}</p>
          </div>
          <div className="text-right text-[11px] text-slate-400 leading-relaxed">
            <p className="text-sm font-bold text-white">{company.name}</p>
            {company.address}<br />{company.phone} · {company.email}<br />{company.creds}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-700">
            <strong>{company.name}</strong> is preparing a prime bid for {solicitation.agency} and is
            requesting {invitation.company_name}&rsquo;s quote for the <strong>{invitation.trade}</strong> scope.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
            {[["Solicitation", solicitation.solicitation_number],
              ["Place of performance", solicitation.facility_address],
              ["Contract term", solicitation.contract_term],
              ["Site walk", milestones.site_walk],
              ["Your quote is due to us", milestones.sub_quote_due],
              ["Prime bid due to agency", solicitation.submission_deadline]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">{k}</span>
                <span className={`font-semibold text-right ${k.startsWith("Your quote") ? "text-blue-700" : "text-slate-800"}`}>
                  {v || "—"}
                </span>
              </div>
            ))}
          </div>
          <a href={`${API}/bid-desk/invite/${token}/rfq.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 mt-4 h-10 px-4 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            data-testid="sub-bid-rfq-link">
            <FileText className="w-4 h-4" />Open the full RFQ packet (PDF)
          </a>
        </section>

        {(scope.key_tasks || []).length > 0 && (
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Scope to price</p>
            <p className="text-sm text-slate-600 mb-3">{scope.summary}</p>
            <ul className="space-y-1.5">
              {scope.key_tasks.map((t, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <Building2 className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" />{t}
                </li>
              ))}
            </ul>
          </section>
        )}

        {done ? (
          <section className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-6 text-center"
            data-testid="sub-bid-thanks">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-bold text-emerald-900">Your quote is in front of our estimating desk.</p>
            <p className="text-sm text-emerald-800/80 mt-1">
              We will be in touch after the agency award. You can still upload backup documents below.
            </p>
            <label className="inline-flex items-center gap-2 mt-4 h-9 px-3 rounded-md bg-white border border-emerald-300 text-sm text-emerald-800 cursor-pointer">
              <Upload className="w-4 h-4" />Upload your quote or backup
              <input type="file" className="hidden"
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </section>
        ) : invitation.status === "declined" ? (
          <section className="rounded-xl border border-slate-200 bg-white p-6 text-center">
            <XCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-600">You let us know you are passing on this one. Thanks for the quick reply.</p>
          </section>
        ) : (
          <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Submit your quote</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["Lump-sum price (USD)", "amount", "number"],
                ["Duration in working days", "duration_days", "number"],
                ["Crew size", "crew_size", "text"],
                ["Your licence number", "license_number", "text"],
                ["Contact name", "contact_name", "text"],
                ["Contact phone", "contact_phone", "text"]].map(([label, key, type]) => (
                <div key={key}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</label>
                  <Input type={type} value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`sub-bid-${key}`} />
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Inclusions</label>
                <Textarea rows={3} value={form.inclusions}
                  onChange={(e) => setForm({ ...form, inclusions: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Exclusions &amp; assumptions</label>
                <Textarea rows={3} value={form.exclusions}
                  onChange={(e) => setForm({ ...form, exclusions: e.target.value })}
                  data-testid="sub-bid-exclusions" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.bonded}
                onChange={(e) => setForm({ ...form, bonded: e.target.checked })}
                data-testid="sub-bid-bonded" />
              We can be bonded for this scope
            </label>
            <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-slate-100">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Upload className="w-4 h-4" />Attach your own quote sheet (optional)
                <input type="file" className="hidden"
                  onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              <div className="flex items-center gap-2">
                <Button variant="ghost" className="text-slate-500" onClick={decline}
                  data-testid="sub-bid-decline">Not bidding</Button>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={submit} disabled={busy}
                  data-testid="sub-bid-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit quote
                </Button>
              </div>
            </div>
          </section>
        )}

        <p className="text-[11px] text-slate-400 text-center pb-6">
          {company.name} · {company.address} · Net 30, pay-when-paid. This request is not an order or
          an award; work proceeds only under an executed subcontract.
        </p>
      </main>
    </div>
  );
}
